import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./WaveSync.css";
import SEO from "../components/SEO";

const features = [
  {
    icon: "🔄",
    title: "Automatic Product Syncing",
    desc: "Keep inventory, prices, and status perfectly aligned between paired products. Choose one-way or bidirectional sync with safe initial sync on setup.",
  },
  {
    icon: "⚡",
    title: "Real-Time Updates",
    desc: "Background auto-sync and webhooks keep data updated without manual work. Never miss a change, even if you're away.",
  },
  {
    icon: "📊",
    title: "Detailed Analytics",
    desc: "View sync history with success, failure, and error information. See per-variant stock and prices for both products in a compact dropdown.",
  },
  {
    icon: "🛡️",
    title: "Safe Initial Sync",
    desc: "When you create or activate a sync rule, Wave Sync performs a safe initial sync to ensure everything is aligned from the start.",
  },
  {
    icon: "🎯",
    title: "Flexible Sync Rules",
    desc: "Sync inventory, prices, and status between any two products. Perfect for multi-channel selling, dropshipping, and inventory management.",
  },
  {
    icon: "🔔",
    title: "Webhook Integration",
    desc: "The app listens for product changes and applies them to the linked product automatically. No manual intervention needed.",
  },
];

const steps = [
  {
    step: "Step 1",
    title: "Install Wave Sync",
    desc: "Find us on the Shopify App Store and click 'Add App'. One-click install, zero configuration required.",
  },
  {
    step: "Step 2",
    title: "Create Sync Rules",
    desc: "Link two products and choose one-way or bidirectional sync. Wave Sync performs a safe initial sync automatically.",
  },
  {
    step: "Step 3",
    title: "Automatic Syncing",
    desc: "Products stay in sync automatically. Background auto-sync and webhooks handle all updates, keeping inventory and prices accurate.",
  },
];

const pricingPlans = [
  {
    name: "Free Plan",
    price: "Free",
    features: [
      "Sync inventory between a small number of products",
      "Webhook-based automatic syncing",
    ],
  },
  {
    name: "Paid Plans",
    price: "Tiered",
    features: [
      "More active sync rules",
      "Expanded sync options and configuration",
      "Analytics and ongoing improvements",
    ],
  },
];

function WaveSync() {
  useEffect(() => {
    document.body.setAttribute("data-page", "wavesync");
    return () => document.body.removeAttribute("data-page");
  }, []);

  return (
    <>
      <SEO />

      <motion.div
        className="wavesync-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.section
          className="wavesync-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-bg-glow" />

          <div className="hero-content">
            <h1>Wave Sync, Product and Inventory Sync for Shopify</h1>
            <h2 className="hero-subtitle">
              Keep products in sync, inventory and prices stay accurate. Link two products and choose one-way or bidirectional sync with automatic updates.
            </h2>
          </div>

          <div className="video-container">
            <iframe
              className="hero-video"
              width="1120"
              height="630"
              src="https://www.youtube.com/embed/25RNQyqBakM?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1"
              title="Wave Sync demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>

          <motion.a
            href="https://apps.shopify.com/wave-sync-1"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button insane pulse"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Add Wave Sync on the Shopify App Store"
          >
            Add to Shopify
          </motion.a>
        </motion.section>

        <section className="wavesync-features">
          <h2 className="section-title">Why Merchants Love Wave Sync</h2>
          <div className="feature-grid">
            {features.map((feat, idx) => (
              <motion.div
                className="feature-card"
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
              >
                <div className="feature-icon">{feat.icon}</div>
                <h3>{feat.title}</h3>
                <p>{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="wavesync-steps">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-wrapper">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="step-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2 }}
              >
                <h4>{step.step}</h4>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 💰 PRICING SECTION */}
        <section className="wavesync-pricing">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <div className="pricing-grid">
            {pricingPlans.map((plan, idx) => (
              <motion.div
                key={idx}
                className={`pricing-card ${plan.popular ? "popular" : ""}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="price">
                  <span className="price-amount">{plan.price}</span>
                  {plan.priceYearly && (
                    <span className="price-yearly">{plan.priceYearly}</span>
                  )}
                </div>
                <ul className="features-list">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx}>✓ {feature}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 🔥 FINAL CTA */}
        <motion.section
          className="wavesync-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="cta-box">
            <h2><span className="text-gradient">Ready to Keep Products in Sync?</span></h2>
            <p>Start using Wave Sync today. Free plan available with automatic syncing for 2 products.</p>
            <motion.a
              href="https://apps.shopify.com/wave-sync-1"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button insane pulse"
              whileHover={{ scale: 1.08 }}
              aria-label="Install Wave Sync from the Shopify App Store"
            >
              Install Wave Sync Free
            </motion.a>
          </div>
        </motion.section>
      </motion.div>
    </>
  );
}

export default WaveSync;

